import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {ActionList, AnchoredOverlay, AvatarStack, Link} from '@primer/react'
import {useState} from 'react'

import {repoStackDetailRoute} from '../../routes/repo-stack-detail-route'
import {normalizeReviewerSlug} from '../overview/reviewers/copilot-reviewer'
import {getAgentAppSlug} from '../PullRequestHeaderAuthors'
import {getStackAuthors, type StackAuthor} from './stack-authors'
import styles from './StackDetailHeader.module.css'

export function StackAuthors() {
  const {data} = useSuspenseRouteQuery(repoStackDetailRoute, 'mainQuery')
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const [isOpen, setIsOpen] = useState(false)
  const authors = getStackAuthors(data.pullRequests)
  const [first, second] = authors
  if (!first) return null

  const authorLinkProps = (author: StackAuthor) => {
    const slug =
      author.isAgent || author.isBot ? getAgentAppSlug({login: normalizeReviewerSlug(author.login)}) : undefined
    const hovercard =
      author.isAgent && slug
        ? getHovercardAttributesForType('copilot', {botSlug: slug})
        : author.isBot
          ? {}
          : getHovercardAttributesForType('user', {login: author.login})
    return {
      href: slug ? `/apps/${slug}` : `/${author.login}`,
      ...hovercard,
    }
  }

  const avatar = (author: StackAuthor, decorative = true) =>
    author.isCopilot ? (
      <CopilotAvatar key={author.login} size="small" isDecorative={decorative} />
    ) : (
      <GitHubAvatar key={author.login} src={`/${author.login}.png`} size={20} alt={decorative ? '' : author.label} />
    )

  const authorLink = (author: StackAuthor, showAvatar = false) => (
    <Link muted inline className={styles.AuthorLink} {...authorLinkProps(author)}>
      {showAvatar && avatar(author)}
      <span className={styles.AuthorName}>{author.label}</span>
    </Link>
  )

  return (
    <>
      {authors.length > 1 && (
        <AvatarStack disableExpand size={20} className={styles.Avatars}>
          {authors.slice(0, 3).map((author, index) => avatar(author, authors.length <= 2 || index === 0))}
        </AvatarStack>
      )}
      {authorLink(first, authors.length === 1)}
      {second && (
        <>
          <span> and </span>
          {authors.length === 2 ? (
            authorLink(second)
          ) : (
            <AnchoredOverlay
              open={isOpen}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              width="auto"
              overlayProps={{role: 'dialog', 'aria-label': 'Stack authors'}}
              focusZoneSettings={{disabled: true}}
              renderAnchor={anchorProps => (
                <Link
                  {...anchorProps}
                  as="button"
                  type="button"
                  muted
                  inline
                  className={styles.OthersButton}
                  aria-haspopup="dialog"
                  aria-label={`${authors.length - 1} others, show all ${authors.length} authors`}
                >
                  {authors.length - 1} others
                </Link>
              )}
            >
              <ActionList>
                <ActionList.Group>
                  <ActionList.GroupHeading as="h2">{authors.length} authors</ActionList.GroupHeading>
                  {authors.map(author => (
                    <ActionList.LinkItem
                      key={author.login}
                      {...authorLinkProps(author)}
                      onClick={() => setIsOpen(false)}
                    >
                      <ActionList.LeadingVisual>{avatar(author)}</ActionList.LeadingVisual>
                      <span className={styles.AuthorLabel}>{author.label}</span>
                      <ActionList.TrailingVisual>
                        {author.count === 1 ? '1 pull request' : `${author.count} pull requests`}
                      </ActionList.TrailingVisual>
                    </ActionList.LinkItem>
                  ))}
                </ActionList.Group>
              </ActionList>
            </AnchoredOverlay>
          )}
        </>
      )}
      <span> stacked </span>
    </>
  )
}
